import { Button, Card, CardContent, CardHeader, CardTitle } from '@recipes/ui';
import { CheckCircle2, Download, Share, SquarePlus, X } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import {
  dismissInstallHint,
  installSnapshot,
  promptInstall,
  subscribeInstall,
  type InstallState,
} from '@/lib/pwa/install';

function useInstallState(): InstallState {
  return useSyncExternalStore(subscribeInstall, installSnapshot, installSnapshot);
}

/** One tap where the browser offers the installation. */
function InstallButton({ size }: { size?: 'sm' }) {
  const { t } = useTranslation();
  const install = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') toast.success(t('pwa.install.done'));
    // "Not now": the hint has done its job – the profile still offers the installation.
    if (outcome === 'dismissed') dismissInstallHint();
  };
  return (
    <Button size={size} onClick={() => void install()} data-testid="install-app">
      <Download aria-hidden /> {t('pwa.install.action')}
    </Button>
  );
}

/** iPhone and iPad have no install button for web pages: the way leads through the share menu. */
function IosSteps() {
  const { t } = useTranslation();
  return (
    <ol className="mt-1 space-y-1 text-sm text-muted-foreground" data-testid="install-ios-steps">
      <li className="flex items-center gap-2">
        <Share className="size-4 shrink-0" aria-hidden /> {t('pwa.install.ios.share')}
      </li>
      <li className="flex items-center gap-2">
        <SquarePlus className="size-4 shrink-0" aria-hidden /> {t('pwa.install.ios.add')}
      </li>
    </ol>
  );
}

/**
 * The hint on the dashboard: "install the app?" – once, until it is installed or closed. Closed
 * means closed on this device; Profile → App keeps the offer.
 */
export function InstallPrompt() {
  const { t } = useTranslation();
  const { mode, dismissed } = useInstallState();
  if (dismissed || (mode !== 'prompt' && mode !== 'ios')) return null;

  return (
    <div
      role="region"
      aria-label={t('pwa.install.title')}
      className="flex items-start gap-3 rounded-lg border bg-accent/40 p-3 text-sm"
      data-testid="install-prompt"
    >
      <Download className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{t('pwa.install.title')}</p>
        <p className="text-muted-foreground">{t('pwa.install.description')}</p>
        {mode === 'ios' && <IosSteps />}
        <p className="mt-1 text-xs text-muted-foreground">{t('pwa.install.laterInProfile')}</p>
      </div>
      {mode === 'prompt' && <InstallButton size="sm" />}
      <Button
        size="icon"
        variant="ghost"
        aria-label={t('pwa.install.dismiss')}
        onClick={dismissInstallHint}
        data-testid="install-dismiss"
      >
        <X aria-hidden />
      </Button>
    </div>
  );
}

/** Profile → App: the same offer for everybody who closed the hint – and what the state is. */
export function InstallAppCard() {
  const { t } = useTranslation();
  const { mode } = useInstallState();
  return (
    <Card data-testid="install-card" data-install-mode={mode}>
      <CardHeader>
        <CardTitle>{t('pwa.install.cardTitle')}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('pwa.install.description')}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {mode === 'installed' && (
          <p className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="size-5 text-success" aria-hidden />
            {t('pwa.install.installed')}
          </p>
        )}
        {mode === 'prompt' && <InstallButton />}
        {mode === 'ios' && (
          <div>
            <p className="text-sm font-medium">{t('pwa.install.ios.title')}</p>
            <IosSteps />
          </div>
        )}
        {mode === 'unavailable' && (
          <p className="text-sm text-muted-foreground">{t('pwa.install.unavailable')}</p>
        )}
        <p className="text-xs text-muted-foreground">{t('pwa.install.updates')}</p>
      </CardContent>
    </Card>
  );
}
